import {Tooltip} from '@dropbox/dig-components/tooltips';
import {Text} from '@dropbox/dig-components/typography';
import {UIIcon} from '@dropbox/dig-icons';
import {
  ChevronDownLine,
  ChevronRightLine,
  PersonCircleLine,
  TeamLine,
} from '@dropbox/dig-icons/assets';
import cx from 'classnames';
import type {RoadmapGroupHierarchyTeam} from 'client';
import {SidebarCollapseToggleButton} from 'components/DSYS/SidebarCollapseToggleButton';
import {Fragment, type ReactElement, useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {PeopleSearch, type PersonOption} from 'views/workstreams/detail/PeopleSearch';

import type {RoadmapNavigationScope} from './navigation';
import type {RoadmapAudience} from './queryParams';
import styles from './Roadmap.module.css';

const TOOLTIP_OFFSET_DISTANCE = 6;
const ROADMAP_SCOPE_SIDEBAR_ROW_PADDING_LEFT = 8;
const ROADMAP_SCOPE_SIDEBAR_NESTED_INDENT = 26;
const SCOPE_CODE_PATTERN = /^([A-Z]{1,4}\d{1,2})(?:\s*[:.\-\u2013\u2014]\s*|\s+)(.+)$/;
export const ROADMAP_SIDEBAR_DISCLOSURE_STORAGE_KEY = 'roadmap-sidebar-disclosure-state-v1';

interface RoadmapSidebarDisclosureState {
  companyExpanded: boolean;
  collapsedScopeKeys: string[];
  groupsExpanded: boolean;
  collapsedGroupTeamIds: string[];
}

const readDisclosureState = (
  defaultState: RoadmapSidebarDisclosureState
): RoadmapSidebarDisclosureState => {
  if (typeof window === 'undefined') return defaultState;

  try {
    const storedValue = window.localStorage.getItem(ROADMAP_SIDEBAR_DISCLOSURE_STORAGE_KEY);
    if (!storedValue) return defaultState;

    const parsed = JSON.parse(storedValue) as Partial<RoadmapSidebarDisclosureState>;
    if (typeof parsed.companyExpanded !== 'boolean' || !Array.isArray(parsed.collapsedScopeKeys)) {
      return defaultState;
    }

    return {
      companyExpanded: parsed.companyExpanded,
      collapsedScopeKeys: [
        ...new Set(
          parsed.collapsedScopeKeys.filter(
            (key): key is string => typeof key === 'string' && key.length > 0 && key.length <= 255
          )
        ),
      ],
      groupsExpanded: typeof parsed.groupsExpanded === 'boolean' ? parsed.groupsExpanded : true,
      collapsedGroupTeamIds: [
        ...new Set(
          (Array.isArray(parsed.collapsedGroupTeamIds) ? parsed.collapsedGroupTeamIds : []).filter(
            (teamId): teamId is string =>
              typeof teamId === 'string' && teamId.length > 0 && teamId.length <= 255
          )
        ),
      ],
    };
  } catch {
    return defaultState;
  }
};

interface RoadmapGroupTreeNode {
  team: RoadmapGroupHierarchyTeam;
  children: RoadmapGroupTreeNode[];
}

const buildGroupTree = (teams: RoadmapGroupHierarchyTeam[]): RoadmapGroupTreeNode[] => {
  const teamsById = new Map(teams.map((team) => [team.team_id, team]));
  const nodesById = new Map(
    teams.map((team) => [team.team_id, {team, children: []} as RoadmapGroupTreeNode])
  );
  const validParentId = (team: RoadmapGroupHierarchyTeam) => {
    let parentId = team.parent_team_id ?? null;
    const seen = new Set([team.team_id]);
    while (parentId && teamsById.has(parentId)) {
      if (seen.has(parentId)) return null;
      seen.add(parentId);
      parentId = teamsById.get(parentId)?.parent_team_id ?? null;
    }
    return team.parent_team_id && teamsById.has(team.parent_team_id) ? team.parent_team_id : null;
  };
  const roots: RoadmapGroupTreeNode[] = [];

  teams.forEach((team) => {
    const node = nodesById.get(team.team_id);
    if (!node) return;
    const parentId = validParentId(team);
    const parent = parentId ? nodesById.get(parentId) : null;
    if (parent) {
      parent.children.push(node);
    } else {
      roots.push(node);
    }
  });

  const sortNodes = (nodes: RoadmapGroupTreeNode[]) => {
    nodes.sort((left, right) =>
      left.team.team_name.localeCompare(right.team.team_name, undefined, {sensitivity: 'base'})
    );
    nodes.forEach((node) => sortNodes(node.children));
  };
  sortNodes(roots);
  return roots;
};

const splitScopeLabel = (label: string, isChild: boolean) => {
  if (!isChild) return {code: null, title: label};

  const match = label.match(SCOPE_CODE_PATTERN);
  if (!match) return {code: null, title: label};

  return {code: match[1], title: match[2]};
};

const groupScopesByParent = (scopes: RoadmapNavigationScope[]) => {
  const groups: Array<{
    root: RoadmapNavigationScope | null;
    children: RoadmapNavigationScope[];
  }> = [];

  scopes.forEach((scope) => {
    if (scope.menuDepth === 0) {
      groups.push({root: scope, children: []});
      return;
    }

    const currentGroup = groups.at(-1);
    if (currentGroup?.root) {
      currentGroup.children.push(scope);
    } else {
      groups.push({root: null, children: [scope]});
    }
  });

  return groups;
};

const RoadmapScopeSidebarItem = ({
  collapsed,
  scope,
  selected,
  expanded,
  hasChildren = false,
  onToggle,
  onSelectScope,
}: {
  collapsed: boolean;
  scope: RoadmapNavigationScope;
  selected: boolean;
  expanded?: boolean;
  hasChildren?: boolean;
  onToggle?: () => void;
  onSelectScope: (scopeKey: string) => void;
}) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const labelRef = useRef<HTMLDivElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const {code, title} = splitScopeLabel(scope.label, scope.menuDepth === 1);

  useEffect(() => {
    const label = labelRef.current;
    if (!label) return;

    const updateTruncation = () => {
      setIsTruncated(label.scrollWidth > label.clientWidth + 1);
    };
    const frame = window.requestAnimationFrame(updateTruncation);

    if (typeof ResizeObserver === 'undefined') {
      return () => window.cancelAnimationFrame(frame);
    }

    const observer = new ResizeObserver(updateTruncation);
    observer.observe(label);
    return () => {
      window.cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [scope.label]);

  const itemClassName = cx(styles.roadmapScopeSidebarItem, {
    [styles.roadmapScopeSidebarItemActive]: selected,
  });
  const shouldIndentUncodedChildScope = scope.menuDepth === 1 && !code;
  const itemStyle = shouldIndentUncodedChildScope
    ? {
        paddingLeft:
          ROADMAP_SCOPE_SIDEBAR_ROW_PADDING_LEFT + 2 * ROADMAP_SCOPE_SIDEBAR_NESTED_INDENT,
      }
    : undefined;
  const label = (
    <div className={styles.roadmapScopeSidebarItemLabel}>
      {scope.menuDepth === 1 && code ? (
        <Text
          aria-hidden="true"
          color="faint"
          size="small"
          className={styles.roadmapScopeSidebarItemCode}
        >
          {code}
        </Text>
      ) : null}
      <div ref={labelRef} className={styles.roadmapScopeSidebarItemTitle}>
        <Text aria-hidden="true" className={styles.roadmapScopeSidebarText}>
          {title}
        </Text>
      </div>
    </div>
  );

  return (
    <>
      {isTruncated ? (
        <Tooltip.Control
          open={isHovered && !collapsed}
          triggerRef={buttonRef}
          placement="right"
          offsetDistance={TOOLTIP_OFFSET_DISTANCE}
        >
          {scope.label}
        </Tooltip.Control>
      ) : null}
      {hasChildren ? (
        <div
          className={itemClassName}
          style={itemStyle}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <button
            type="button"
            tabIndex={collapsed ? -1 : 0}
            aria-label={`${expanded ? 'Collapse' : 'Expand'} ${scope.label} initiatives`}
            aria-expanded={expanded}
            className={styles.roadmapScopeSidebarCompanyChevron}
            onClick={(event) => {
              event.stopPropagation();
              onToggle?.();
            }}
          >
            <UIIcon
              isOpticallyAligned
              src={expanded ? ChevronDownLine : ChevronRightLine}
              size="small"
            />
          </button>
          <button
            ref={buttonRef}
            type="button"
            tabIndex={collapsed ? -1 : 0}
            aria-label={scope.label}
            aria-current={selected ? 'page' : undefined}
            className={styles.roadmapScopeSidebarCompanySelect}
            onClick={() => onSelectScope(scope.key)}
          >
            {label}
          </button>
        </div>
      ) : (
        <button
          ref={buttonRef}
          type="button"
          tabIndex={collapsed ? -1 : 0}
          aria-label={scope.label}
          aria-current={selected ? 'page' : undefined}
          className={itemClassName}
          style={itemStyle}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          onClick={() => onSelectScope(scope.key)}
        >
          {label}
        </button>
      )}
    </>
  );
};

const RoadmapGroupSidebarItem = ({
  collapsed,
  node,
  depth,
  selected,
  expanded,
  onToggle,
  onSelectTeam,
}: {
  collapsed: boolean;
  node: RoadmapGroupTreeNode;
  depth: number;
  selected: boolean;
  expanded: boolean;
  onToggle: () => void;
  onSelectTeam: (team: RoadmapGroupHierarchyTeam) => void;
}) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const labelRef = useRef<HTMLDivElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const hasChildren = node.children.length > 0;
  const activateNode = () => {
    if ((node.team.group_ids ?? []).length > 0) {
      onSelectTeam(node.team);
    } else if (hasChildren) {
      onToggle();
    }
  };
  const itemClassName = cx(styles.roadmapScopeSidebarItem, {
    [styles.roadmapScopeSidebarItemActive]: selected,
  });
  const itemStyle = {
    paddingLeft:
      ROADMAP_SCOPE_SIDEBAR_ROW_PADDING_LEFT + (depth + 1) * ROADMAP_SCOPE_SIDEBAR_NESTED_INDENT,
  };

  useEffect(() => {
    const label = labelRef.current;
    if (!label) return;
    const updateTruncation = () => setIsTruncated(label.scrollWidth > label.clientWidth + 1);
    const frame = window.requestAnimationFrame(updateTruncation);
    if (typeof ResizeObserver === 'undefined') {
      return () => window.cancelAnimationFrame(frame);
    }
    const observer = new ResizeObserver(updateTruncation);
    observer.observe(label);
    return () => {
      window.cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [node.team.team_name]);

  const label = (
    <div ref={labelRef} className={styles.roadmapScopeSidebarItemTitle}>
      <Text aria-hidden="true" className={styles.roadmapScopeSidebarText}>
        {node.team.team_name}
      </Text>
    </div>
  );

  return (
    <>
      {isTruncated ? (
        <Tooltip.Control
          open={isHovered && !collapsed}
          triggerRef={buttonRef}
          placement="right"
          offsetDistance={TOOLTIP_OFFSET_DISTANCE}
        >
          {node.team.team_name}
        </Tooltip.Control>
      ) : null}
      {hasChildren ? (
        <div
          className={itemClassName}
          style={itemStyle}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <button
            type="button"
            tabIndex={collapsed ? -1 : 0}
            aria-label={`${expanded ? 'Collapse' : 'Expand'} ${node.team.team_name} groups`}
            aria-expanded={expanded}
            className={styles.roadmapScopeSidebarCompanyChevron}
            onClick={(event) => {
              event.stopPropagation();
              onToggle();
            }}
          >
            <UIIcon
              isOpticallyAligned
              src={expanded ? ChevronDownLine : ChevronRightLine}
              size="small"
            />
          </button>
          <button
            ref={buttonRef}
            type="button"
            tabIndex={collapsed ? -1 : 0}
            aria-label={node.team.team_name}
            aria-current={selected ? 'page' : undefined}
            className={styles.roadmapScopeSidebarCompanySelect}
            onClick={activateNode}
          >
            {label}
          </button>
        </div>
      ) : (
        <button
          ref={buttonRef}
          type="button"
          tabIndex={collapsed ? -1 : 0}
          aria-label={node.team.team_name}
          aria-current={selected ? 'page' : undefined}
          disabled={(node.team.group_ids ?? []).length === 0}
          className={itemClassName}
          style={itemStyle}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          onClick={activateNode}
        >
          {label}
        </button>
      )}
    </>
  );
};

export const RoadmapScopeSidebar = ({
  collapsed,
  scopes,
  selectedAudience,
  selectedScopeKey,
  selectedPersonLdap,
  groupHierarchy = [],
  selectedGroupTeamId = null,
  onToggle,
  onSelectMyRoadmap,
  onSelectPerson,
  onSelectTeam = () => undefined,
  onSelectCompany,
  onSelectScope,
}: {
  collapsed: boolean;
  scopes: RoadmapNavigationScope[];
  selectedAudience: RoadmapAudience;
  selectedScopeKey: string | null;
  selectedPersonLdap: string | null;
  groupHierarchy?: RoadmapGroupHierarchyTeam[];
  selectedGroupTeamId?: string | null;
  onToggle: () => void;
  onSelectMyRoadmap: () => void;
  onSelectPerson: (person: PersonOption) => void;
  onSelectTeam?: (team: RoadmapGroupHierarchyTeam) => void;
  onSelectCompany: () => void;
  onSelectScope: (scopeKey: string) => void;
}) => {
  const {t} = useTranslation();
  const myRoadmapLabel = t('my_roadmap').toString();
  const companyLabel = t('company').toString();
  const groupTree = useMemo(() => buildGroupTree(groupHierarchy), [groupHierarchy]);
  const promotedGroupRoot = useMemo(
    () =>
      groupTree.find(
        (node) => node.team.team_name.trim().toLocaleLowerCase() === 'dropbox teams'
      ) ?? null,
    [groupTree]
  );
  const groupsLabel = 'Groups';
  const displayedGroupTree = useMemo(
    () =>
      promotedGroupRoot
        ? [
            ...promotedGroupRoot.children,
            ...groupTree.filter((node) => node.team.team_id !== promotedGroupRoot.team.team_id),
          ]
        : groupTree,
    [groupTree, promotedGroupRoot]
  );
  const defaultDisclosureState = useMemo(() => {
    const collapsibleGroupTeamIds: string[] = [];
    const collectCollapsibleGroupTeamIds = (nodes: RoadmapGroupTreeNode[]) => {
      nodes.forEach((node) => {
        if (node.children.length > 0) {
          collapsibleGroupTeamIds.push(node.team.team_id);
          collectCollapsibleGroupTeamIds(node.children);
        }
      });
    };
    collectCollapsibleGroupTeamIds(displayedGroupTree);

    return {
      companyExpanded: true,
      collapsedScopeKeys: groupScopesByParent(scopes).flatMap(({root, children}) =>
        root && children.length > 0 ? [root.key] : []
      ),
      groupsExpanded: true,
      collapsedGroupTeamIds: collapsibleGroupTeamIds,
    };
  }, [displayedGroupTree, scopes]);
  const [disclosureState, setDisclosureState] = useState(() =>
    readDisclosureState(defaultDisclosureState)
  );
  const promotedGroupRootSelected =
    selectedAudience === 'group' && promotedGroupRoot?.team.team_id === selectedGroupTeamId;
  const collapsedScopeKeys = new Set(disclosureState.collapsedScopeKeys);
  const collapsedGroupTeamIds = new Set(disclosureState.collapsedGroupTeamIds);

  useEffect(() => {
    try {
      window.localStorage.setItem(
        ROADMAP_SIDEBAR_DISCLOSURE_STORAGE_KEY,
        JSON.stringify(disclosureState)
      );
    } catch {
      // Storage can be unavailable in restricted browser contexts; keep the in-memory state.
    }
  }, [disclosureState]);

  const toggleCompany = () =>
    setDisclosureState((current) => ({
      ...current,
      companyExpanded: !current.companyExpanded,
    }));
  const toggleGroups = () =>
    setDisclosureState((current) => ({
      ...current,
      groupsExpanded: !current.groupsExpanded,
    }));
  const toggleGroupTeam = (teamId: string) => {
    setDisclosureState((current) => {
      const next = new Set(current.collapsedGroupTeamIds);
      if (next.has(teamId)) {
        next.delete(teamId);
      } else {
        next.add(teamId);
      }
      return {...current, collapsedGroupTeamIds: [...next]};
    });
  };
  const toggleScope = (scopeKey: string) => {
    setDisclosureState((current) => {
      const next = new Set(current.collapsedScopeKeys);
      if (next.has(scopeKey)) {
        next.delete(scopeKey);
      } else {
        next.add(scopeKey);
      }
      return {...current, collapsedScopeKeys: [...next]};
    });
  };
  const renderGroupNodes = (nodes: RoadmapGroupTreeNode[], depth = 0): ReactElement[] =>
    nodes.flatMap((node) => {
      const expanded = !collapsedGroupTeamIds.has(node.team.team_id);
      return [
        <RoadmapGroupSidebarItem
          key={node.team.team_id}
          collapsed={collapsed}
          node={node}
          depth={depth}
          selected={selectedAudience === 'group' && node.team.team_id === selectedGroupTeamId}
          expanded={expanded}
          onToggle={() => toggleGroupTeam(node.team.team_id)}
          onSelectTeam={onSelectTeam}
        />,
        ...(expanded ? renderGroupNodes(node.children, depth + 1) : []),
      ];
    });

  return (
    <>
      <SidebarCollapseToggleButton
        collapsed={collapsed}
        onToggle={onToggle}
        className={styles.roadmapScopeSidebarToggle}
        collapsedClassName={styles.roadmapScopeSidebarToggleCollapsed}
        iconButtonClassName={styles.roadmapScopeSidebarToggleButton}
      />
      <aside
        aria-label="Roadmap scopes"
        aria-hidden={collapsed}
        className={cx(styles.roadmapScopeSidebar, {
          [styles.roadmapScopeSidebarCollapsed]: collapsed,
        })}
      >
        <nav className={styles.roadmapScopeSidebarNav}>
          <div className={styles.roadmapScopeSidebarPrimaryItems}>
            <button
              type="button"
              tabIndex={collapsed ? -1 : 0}
              aria-label={myRoadmapLabel}
              aria-current={selectedAudience === 'my' ? 'page' : undefined}
              className={cx(styles.roadmapScopeSidebarItem, {
                [styles.roadmapScopeSidebarItemActive]: selectedAudience === 'my',
              })}
              onClick={onSelectMyRoadmap}
            >
              <span className={styles.roadmapScopeSidebarDestinationIcon}>
                <UIIcon isOpticallyAligned src={PersonCircleLine} size="medium" />
              </span>
              <Text aria-hidden="true" className={styles.roadmapScopeSidebarText}>
                {myRoadmapLabel}
              </Text>
            </button>

            {!collapsed ? (
              <>
                <PeopleSearch
                  isNested={false}
                  selectedPersonLdap={selectedPersonLdap}
                  onSelectPerson={onSelectPerson}
                />
              </>
            ) : null}

            {groupTree.length ? (
              <>
                <div
                  className={cx(
                    styles.roadmapScopeSidebarItem,
                    styles.roadmapScopeSidebarCompanyItem,
                    {
                      [styles.roadmapScopeSidebarItemActive]: promotedGroupRootSelected,
                    }
                  )}
                >
                  <button
                    type="button"
                    tabIndex={collapsed ? -1 : 0}
                    aria-label={`${disclosureState.groupsExpanded ? 'Collapse' : 'Expand'} Groups`}
                    aria-expanded={disclosureState.groupsExpanded}
                    className={styles.roadmapScopeSidebarCompanyChevron}
                    onClick={(event) => {
                      event.stopPropagation();
                      toggleGroups();
                    }}
                  >
                    <UIIcon
                      isOpticallyAligned
                      src={disclosureState.groupsExpanded ? ChevronDownLine : ChevronRightLine}
                      size="small"
                    />
                  </button>
                  <button
                    type="button"
                    tabIndex={collapsed ? -1 : 0}
                    aria-label={groupsLabel}
                    aria-current={promotedGroupRootSelected ? 'page' : undefined}
                    className={styles.roadmapScopeSidebarCompanySelect}
                    onClick={() => {
                      if (
                        promotedGroupRoot &&
                        (promotedGroupRoot.team.group_ids ?? []).length > 0
                      ) {
                        onSelectTeam(promotedGroupRoot.team);
                      } else {
                        toggleGroups();
                      }
                    }}
                  >
                    <span className={styles.roadmapScopeSidebarDestinationIcon}>
                      <UIIcon isOpticallyAligned src={TeamLine} size="medium" />
                    </span>
                    <Text aria-hidden="true" className={styles.roadmapScopeSidebarText}>
                      {groupsLabel}
                    </Text>
                  </button>
                </div>
                {disclosureState.groupsExpanded ? (
                  <div className={styles.roadmapScopeSidebarGroupScopes}>
                    {renderGroupNodes(displayedGroupTree)}
                  </div>
                ) : null}
              </>
            ) : null}

            <div
              className={cx(styles.roadmapScopeSidebarItem, styles.roadmapScopeSidebarCompanyItem, {
                [styles.roadmapScopeSidebarItemActive]: selectedAudience === 'company',
              })}
            >
              <button
                type="button"
                tabIndex={collapsed ? -1 : 0}
                aria-label={`${disclosureState.companyExpanded ? 'Collapse' : 'Expand'} Company initiatives`}
                aria-expanded={disclosureState.companyExpanded}
                className={styles.roadmapScopeSidebarCompanyChevron}
                onClick={(event) => {
                  event.stopPropagation();
                  toggleCompany();
                }}
              >
                <UIIcon
                  isOpticallyAligned
                  src={disclosureState.companyExpanded ? ChevronDownLine : ChevronRightLine}
                  size="small"
                />
              </button>
              <button
                type="button"
                tabIndex={collapsed ? -1 : 0}
                aria-label={companyLabel}
                aria-current={selectedAudience === 'company' ? 'page' : undefined}
                className={styles.roadmapScopeSidebarCompanySelect}
                onClick={onSelectCompany}
              >
                <span className={styles.roadmapScopeSidebarDestinationIcon}>
                  <UIIcon isOpticallyAligned src={TeamLine} size="medium" />
                </span>
                <Text aria-hidden="true" className={styles.roadmapScopeSidebarText}>
                  {companyLabel}
                </Text>
              </button>
            </div>
          </div>

          {disclosureState.companyExpanded ? (
            <div className={styles.roadmapScopeSidebarCompanyScopes}>
              {groupScopesByParent(scopes).map(({root, children}) => {
                if (!root) {
                  return children.map((scope) => (
                    <RoadmapScopeSidebarItem
                      key={scope.key}
                      collapsed={collapsed}
                      scope={scope}
                      selected={selectedAudience === 'scope' && scope.key === selectedScopeKey}
                      onSelectScope={onSelectScope}
                    />
                  ));
                }

                const expanded = !collapsedScopeKeys.has(root.key);
                return (
                  <Fragment key={root.key}>
                    <RoadmapScopeSidebarItem
                      collapsed={collapsed}
                      scope={root}
                      selected={selectedAudience === 'scope' && root.key === selectedScopeKey}
                      expanded={expanded}
                      hasChildren={children.length > 0}
                      onToggle={() => toggleScope(root.key)}
                      onSelectScope={onSelectScope}
                    />
                    {expanded
                      ? children.map((scope) => (
                          <RoadmapScopeSidebarItem
                            key={scope.key}
                            collapsed={collapsed}
                            scope={scope}
                            selected={
                              selectedAudience === 'scope' && scope.key === selectedScopeKey
                            }
                            onSelectScope={onSelectScope}
                          />
                        ))
                      : null}
                  </Fragment>
                );
              })}
            </div>
          ) : null}
        </nav>
      </aside>
    </>
  );
};
